import { WorkflowTypes, isCrmWorkflow } from '@common/interfaces/notes'
import { UserRole } from '@common/interfaces/user'

/** Header types its `workflow` prop as a plain string, so keep the comparison string-to-string. */
const CLIENT_PAGE_WORKFLOW: string = WorkflowTypes.clientPage

/**
 * Whether the right column is suppressed for this caller on this page.
 *
 * The client page's column is loan servicing — BBC flags, borrowing-base logs,
 * reporting notes — and BD reaches that page for the company Overview alone, so
 * there is nothing in it for them. Their own workflows keep it.
 *
 * Shared because suppression has two halves that must agree: MainLayout skips
 * mounting the column, and the Header must skip its toggle, or the button is left
 * flipping state nothing reads. The participant suppression works the same way.
 */
export const isNotesColumnSuppressed = (
  role: UserRole | undefined,
  workflow: string | undefined,
): boolean => role === UserRole.BDO && workflow === CLIENT_PAGE_WORKFLOW

/**
 * Notes right-column panel model.
 *
 * The column has three presentation states at the redux layer:
 *   - 'hidden' : not present at all (no rail, no panel) — pages that deliberately
 *                suppress the column, e.g. the legal-document viewer
 *   - 'rail'   : collapsed to the thin right-edge rail
 *   - 'docked' : the expanded panel
 *
 * We store the CURRENT effective state in redux (see notes reducer
 * `notesPanelState`) and derive `isNotesShown = state === 'docked'` so the ~15
 * existing consumers that reflow off `isNotesShown` keep working unchanged. The
 * per-workflow REMEMBERED default lives in localStorage (below) and is applied
 * by the resolver on navigation — a forced-open deep-link sets the current
 * state without touching the remembered default.
 */
export enum NotesPanelState {
  hidden = 'hidden',
  rail = 'rail',
  docked = 'docked',
}

/**
 * The subset of states the user's remembered per-workflow preference can take.
 * 'hidden' is a per-page suppression, never a stored choice.
 */
export type NotesPanelPreference = NotesPanelState.rail | NotesPanelState.docked

export const DEFAULT_NOTES_PANEL_WIDTH = 352
export const MIN_NOTES_PANEL_WIDTH = 300
export const MAX_NOTES_PANEL_WIDTH = 640

/** Width of the collapsed rail. Mirrored by `$notesRailWidth` in variables.scss. */
export const NOTES_RAIL_WIDTH = 56

/**
 * The right column's tabs. Lives here rather than inside RightSidebar so the
 * callers that need to name a tab — deep-link handling in MainLayout, the rail's
 * icon lookup — share one definition instead of passing bare strings around.
 *
 * The values are the tab labels, which is what MUI's TabContext matches on.
 */
export enum NotesPanelTab {
  notes = 'Notes',
  flags = 'Flags',
  news = 'News',
  marketIntel = 'Intel',
  logs = 'Logs',
  tasks = 'Tasks',
}

/** The `?panel=` deep link names a tab by its lowercased label (`notes`, `tasks`, `intel`, …). */
export const notesPanelTabFromParam = (param: string | null): NotesPanelTab | null =>
  Object.values(NotesPanelTab).find((tab) => tab.toLowerCase() === param?.toLowerCase()) ?? null

/** Viewport width (px) at/above which we treat the screen as desktop-class. */
export const NOTES_DESKTOP_MIN_WIDTH = 1440

/**
 * Below this width an expanded panel overlays the content (with a scrim) instead
 * of squeezing it, so dense tables keep their full width on a laptop. Above it
 * the panel pushes content as it always has.
 */
export const NOTES_OVERLAY_MAX_WIDTH = NOTES_DESKTOP_MIN_WIDTH

/**
 * Workflows where notes are core to the task and worth defaulting open on a
 * desktop-class screen. Everything else defaults to the rail.
 */
export const NOTE_HEAVY_WORKFLOWS: WorkflowTypes[] = [
  WorkflowTypes.bbc,
  WorkflowTypes.clientPage,
  WorkflowTypes.dueDiligencePage,
]

export const isNoteHeavyWorkflow = (workflow: WorkflowTypes | null | undefined): boolean =>
  !!workflow && NOTE_HEAVY_WORKFLOWS.includes(workflow)

/**
 * Workflows for a list page rather than a single record. There is no `:id` in
 * the route, so there is nothing for notes or flags to hang off — News is the
 * whole content of the panel on these pages.
 */
export const LIST_PAGE_WORKFLOWS: WorkflowTypes[] = [
  WorkflowTypes.homePage,
  WorkflowTypes.clientManagement,
  WorkflowTypes.prospectsList,
]

export const isListPageWorkflow = (workflow: WorkflowTypes | null | undefined): boolean =>
  !!workflow && LIST_PAGE_WORKFLOWS.includes(workflow)

/**
 * Workflows whose sidebar offers a Flags tab. List pages show flags full-page
 * instead, and CRM records have none, so a `?flagId=` there must not force the
 * panel open onto a tab that doesn't exist.
 */
export const isFlagsCapableWorkflow = (workflow: WorkflowTypes | null | undefined): boolean =>
  !!workflow && !isListPageWorkflow(workflow) && !isCrmWorkflow(workflow)

/**
 * Workflows whose sidebar offers a News tab: the list pages, plus the two record
 * pages whose feed is scoped to that record. Single source of truth for the tab
 * strip, the rail and NewsContainer's own fetch — those three listed these
 * workflows separately and could drift apart, which is how the prospects queue
 * ended up with no News tab at all.
 */
export const isNewsCapableWorkflow = (workflow: WorkflowTypes | null | undefined): boolean =>
  isListPageWorkflow(workflow) ||
  workflow === WorkflowTypes.clientPage ||
  workflow === WorkflowTypes.entityPage

interface ResolveNotesDefaultArgs {
  workflow: WorkflowTypes | null | undefined
  isDesktop: boolean
  /** The user's remembered choice for this workflow, if any. Wins over the default. */
  persistedDefault: NotesPanelPreference | null
  /** Client-portal (borrower) users keep today's open-by-default behavior. */
  isClientPortal: boolean
}

/**
 * Pure resolver for the panel's default state on a given page. Forced-open
 * (cell/flag/noteId deep-links) is applied on top of this result at the call
 * site, not inside here.
 */
export const resolveNotesDefault = ({
  workflow,
  isDesktop,
  persistedDefault,
  isClientPortal,
}: ResolveNotesDefaultArgs): NotesPanelPreference => {
  // Out of scope for the rail redesign — borrowers keep the old behavior.
  if (isClientPortal) {
    return NotesPanelState.docked
  }
  // An explicit user choice for this workflow always wins.
  if (persistedDefault) {
    return persistedDefault
  }
  // Note-heavy workflows earn the docked panel on a roomy screen.
  if (isNoteHeavyWorkflow(workflow) && isDesktop) {
    return NotesPanelState.docked
  }
  return NotesPanelState.rail
}

// --- cell-note scope ----------------------------------------------------------

/**
 * Query param the blue cell-note icon writes to scope the panel to a single cell.
 * `NotesContainer` derives its entire "cell notes mode" from this param, so the
 * param IS the filter — dropping it is the only way back to the full list.
 */
export const CELL_NOTE_CONTEXT_PARAM = 'cellNoteContext'

/**
 * Remove the cell-note scope from a query string, returning the remainder.
 *
 * URLSearchParams rather than `query-string` on purpose: this module is kept
 * dependency-free so it can be unit-tested in the server jest project (the only
 * runner this repo has), and query-string v9 is ESM-only.
 *
 * Returns the input untouched when the param isn't present, so callers can skip
 * navigating entirely.
 */
export const stripCellNoteContext = (search: string): string => {
  const params = new URLSearchParams(search)
  if (!params.has(CELL_NOTE_CONTEXT_PARAM)) {
    return search
  }
  params.delete(CELL_NOTE_CONTEXT_PARAM)
  return params.toString()
}

/** The cell-scoped params of a notes list request. */
export interface ICellNotesListScope {
  tableType?: string
  rowId?: string
  columnIds?: string[]
  recordDate?: string
}

/**
 * Which cell a notes list was fetched for, or undefined for a list that isn't cell-scoped. The
 * drawer compares it with the open cell so a switch never shows the previous cell's notes.
 */
export const getCellNotesListKey = ({
  tableType,
  rowId,
  columnIds,
  recordDate,
}: ICellNotesListScope): string | undefined =>
  tableType
    ? JSON.stringify([tableType, rowId ?? '', columnIds ?? [], recordDate ?? ''])
    : undefined

// --- localStorage persistence (best-effort; never throws) ---------------------

const PREF_KEY = 'atlas.notesPanel.preference.v1'

/**
 * Where the docked width is remembered. Owned by `useResizablePanelWidth` (shared
 * with the task detail panel), which handles clamping and persistence — this
 * module only names the key so the value survives from before that switch.
 */
export const NOTES_PANEL_WIDTH_STORAGE_KEY = 'atlas.notesPanel.width.v1'

// 'hidden' is never a remembered preference — it is a per-page suppression, not a
// choice the user made about how they like the column.
const isPreference = (value: unknown): value is NotesPanelPreference =>
  value === NotesPanelState.rail || value === NotesPanelState.docked

const readPrefMap = (): Record<string, NotesPanelPreference> => {
  try {
    const raw = window.localStorage.getItem(PREF_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, unknown>
    return Object.entries(parsed).reduce<Record<string, NotesPanelPreference>>(
      (acc, [key, value]) => {
        if (isPreference(value)) {
          acc[key] = value
        }
        return acc
      },
      {},
    )
  } catch {
    return {}
  }
}

/** The remembered panel state for a workflow, or null if none / unavailable. */
export const getNotesPanelPreference = (
  workflow: WorkflowTypes | null | undefined,
): NotesPanelPreference | null => {
  if (!workflow) return null
  const map = readPrefMap()
  return map[workflow] ?? null
}

/** Remember the user's explicit choice for a workflow. */
export const setNotesPanelPreference = (
  workflow: WorkflowTypes | null | undefined,
  state: NotesPanelPreference,
): void => {
  if (!workflow) return
  try {
    const map = readPrefMap()
    map[workflow] = state
    window.localStorage.setItem(PREF_KEY, JSON.stringify(map))
  } catch {
    // ignore (private mode / quota / unavailable)
  }
}

// --- layout custom properties -------------------------------------------------

/**
 * The notes column's footprint, published to CSS so every `100vw`-based width
 * calc in the app stays correct without each component knowing the panel state.
 *
 * Three values because they answer three different questions:
 *   - `railWidth`  : how much the rail covers at the right edge (it is fixed, so
 *                    content must subtract it or slide underneath)
 *   - `panelWidth` : how wide the docked panel itself is (drawer, note-expand and
 *                    BBC-log modals, header offset)
 *   - `pushWidth`  : how much the panel actually steals from content — the panel
 *                    width when it pushes, 0 when it overlays or is collapsed
 */
export interface INotesLayoutVars {
  railWidth: number
  panelWidth: number
  pushWidth: number
}

interface ResolveNotesLayoutArgs {
  state: NotesPanelState
  width: number
  /** Expanded panel floats over content instead of squeezing it (laptop widths). */
  isOverlay: boolean
  /** Below md the column is a full-screen slide-in and the rail is suppressed. */
  isMobile: boolean
  /**
   * Whether the rail actually renders. False when the page offers it no tabs —
   * an invisible rail must not reserve width. Defaults true so callers that
   * always render one don't have to say so.
   */
  isRailVisible?: boolean
}

export const resolveNotesLayout = ({
  state,
  width,
  isOverlay,
  isMobile,
  isRailVisible = true,
}: ResolveNotesLayoutArgs): INotesLayoutVars => {
  if (state === NotesPanelState.hidden) {
    return { railWidth: 0, panelWidth: width, pushWidth: 0 }
  }
  if (state === NotesPanelState.rail) {
    return {
      railWidth: isMobile || !isRailVisible ? 0 : NOTES_RAIL_WIDTH,
      panelWidth: width,
      pushWidth: 0,
    }
  }
  return {
    railWidth: 0,
    panelWidth: width,
    pushWidth: isOverlay || isMobile ? 0 : width,
  }
}

const RAIL_WIDTH_VAR = '--notes-rail-width'
const PANEL_WIDTH_VAR = '--notes-panel-width'
const PUSH_WIDTH_VAR = '--notes-push-width'

/**
 * Written to the document root (not a component subtree) because the modals that
 * offset off the panel width render through portals outside the layout tree.
 */
export const applyNotesLayoutVars = ({
  railWidth,
  panelWidth,
  pushWidth,
}: INotesLayoutVars): void => {
  const { style } = document.documentElement
  style.setProperty(RAIL_WIDTH_VAR, `${railWidth}px`)
  style.setProperty(PANEL_WIDTH_VAR, `${panelWidth}px`)
  style.setProperty(PUSH_WIDTH_VAR, `${pushWidth}px`)
}

/** Reset to "no notes column" — used when the sidebar unmounts. */
export const clearNotesLayoutVars = (): void => {
  applyNotesLayoutVars({ railWidth: 0, panelWidth: DEFAULT_NOTES_PANEL_WIDTH, pushWidth: 0 })
}
